import router from "@/router";
import { usePosSetupStore } from "@/store/posSetupStore";
import { DocketsRecord, pb } from "@/utils/PocketBaseAdapter";
import { markEventTicketsPaidForDocket } from "@/utils/markEventTicketsPaid";
import { useServerPerson } from '@/utils/useServerPerson';
import isEmpty from "lodash/isEmpty";
import { defineStore } from "pinia";
import { toast } from "vue3-toastify";

export const useCheckoutStore = defineStore("CheckoutStore", {
  state: () => ({
    payingNow: 0,
    isCashValidation: false,
    balanceToPay: 0,
    remainingBalance: 0, // this will use in split tab to store remaining balance after split amount deduct
    amtTender: 0,
    activeTab: 0,
    splitBy: 1,
    customeSplitValue: 0,
    intialSplit: 0,
    showSpinner: true,
    docketLinesItems: [],
    processingItems: [],
    originalDocketItems: [],
    paymentStatus: null,
    selectedItem: null,
    selectedProcessingItem: null,
    availableItems: [],
    selectedTime: '',
    selectedDate: '',
    discount: 0,
    lineTotals: 0,
    dockets: [] as DocketsRecord[],
    activePaymentTab: 0,
    partialPayingAmount: 0,
    paymentStatusForSplit: "",
    isFullScreenLoading: false,
    docketNumber: 0,
    selectedMembership: {},
    docketDescription: "",
    docketPaymentId: "", // payment id after initial payment process
    groupOrder: false,
    showPreventBenefits: false,
    discountsAreZero: false,
    fobDetails: {
      isVisible: false,
      userId: null as string | null,
      userName: "",
      membership: "",
      productDiscount: 0,
      bookingDiscount: 0,
      eventDiscount: 0,
      foodDiscount: 0,
      loyaltyBalance: 0,
      tempBalance: 0,
    },
    loading: false,
    discountSources: [],
    MARK_AS_PAID: {
      isVisible: false,
      message: "",
      onClick: () => {},
    },
    posDiscount: {
      discountProducts: 0,
      discountBookings: 0,
      discountEvents: 0,
      discountFood: 0,
    },
  }),
  actions: {
    // Function to handle Fob discount
    async handleFobDiscount(
      userId: string,
      docketId?: string,
    ) {
      try {
        let discountData = {
          discountProducts: 0,
          discountBookings: 0,
          discountEvents: 0,
          discountFood: 0,
          user: '',
        };
        // Check if userId exists in fobDetails. Mutate fields instead of replacing object
        if (!userId) {
          this.fobDetails.isVisible = false;
          this.fobDetails.userId = null;
          this.fobDetails.userName = "";
          this.fobDetails.membership = "";
          this.fobDetails.loyaltyBalance = 0;
          this.fobDetails.tempBalance = 0;
        } else {
          // Fetch user membership data
          const userData = await pb.collection("users").getOne(userId, {
            fields: "id,membership,expand,firstName,lastName,username,preventBenefits,loyaltyBalance,tempBalance",
            expand: "membership",
            requestKey: null,
          });

          const { id, firstName, lastName, username } = userData;
          this.fobDetails.userName = `${firstName} ${lastName}` || username;
          this.fobDetails.userId = id;
          this.fobDetails.loyaltyBalance = userData?.loyaltyBalance || 0;
          this.fobDetails.tempBalance = userData?.tempBalance || 0;
          discountData.user = id;

          if(userData?.preventBenefits) {
            this.showPreventBenefits = true
            this.discountsAreZero = true
          } else {
            this.showPreventBenefits = false
            this.discountsAreZero = false
          }

          // Update fobDetails if membership data is available
          if (userData && !isEmpty(userData?.expand?.membership) && !userData?.preventBenefits) {
            const {
              id,
              discountProduct,
              discountFurtherBookings,
              discountEvents,
              discountFood,
            } = userData?.expand?.membership;

            this.fobDetails.membership = id;

            discountData.discountProducts = discountProduct || 0
            discountData.discountBookings = discountFurtherBookings || 0
            discountData.discountEvents = discountEvents || 0
            discountData.discountFood = discountFood || 0
          } else {
            this.fobDetails.membership = "";
          }
        }

        const data = await pb.send('/bbapi/pos/applyDiscount', {
          method: 'POST',
          body: { 
            "docket": docketId,
            "discountData": discountData,
            "type": "Membership",
          },
        });

        if (!data.success) {
          const errorMessage = data.error || `Server error: ${data.status}`;
          console.error("API error:", errorMessage);
          
          toast.error(`Failed to apply discount: ${errorMessage}`, {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 5000,
          });
          
          throw new Error(errorMessage);
        } else {
          let message = "";
          if (!userId) {
            message = "Membership discount removed.";
          } else if (data.applied && this.fobDetails.membership === "") {
            message = "Membership has no discounts to apply.";
          } else if(data.applied && userId) {
            message = "Membership discount applied successfully.";
          } else if(!data.applied && userId) {
            message = "A higher discount has already been applied. The current discount code will not replace the existing discount.";
          }

          toast.success(
            message,
            { position: toast.POSITION.BOTTOM_RIGHT, autoClose:3000 },
          );
        }
      } catch (error) {
        console.error("Error fetching fob discount details:", error);
      }
    },

    async subscribeUnSubscribeAccessReaders(type: string) {
      try {
        if (type === "subscribe") {
          const posSetupStore = usePosSetupStore();
          const posSetup = await posSetupStore.load();
          const { setServerPerson, serverPersonId } = useServerPerson();
          if(posSetup?.accessReader){
            pb.collection("accessReaders").subscribe("*", async (e) => {
              if (e && e?.record && posSetup?.accessReader === e ?.record?.id) {
                const { lastUser } = e?.record;
                const isScanningForServerPerson = !serverPersonId.value && e?.record?.expand?.lastUser?.roles.length > 0;
                if(isScanningForServerPerson){
                  await setServerPerson(lastUser);
                } else {
                  this.fobDetails.userId = lastUser;
                  const docket = this.docketLinesItems[0].docket
                  await this.handleFobDiscount(lastUser, docket);
                }
              }
            }, { expand: 'lastUser' });
          }
        } else {
          pb.collection("accessReaders").unsubscribe("*");
        }
      } catch (e) {
        console.error(e);
      }
    },

    async checkDocketDues(due: number) {
      try {
        if (due <= 0) {
          this.MARK_AS_PAID = {
            isVisible: true,
            message:
              "Docket now has a $" + due + " balance do you want to mark it as paid?",
            onClick: async (did: string = "") => {
              await this.markDocketAsPaid(did);
            },
          };
        }
      } catch (e) {}
    },

    async markDocketAsPaid(did: string) {
      try {
        if (!did) {
          return;
        }

        // Flip any already-existing event tickets on this docket (eg. pre-created
        // unpaid by a webshop "pay in store" purchase) to paid, since this path
        // bypasses DocketStore.ts's paymentApproved().
        await markEventTicketsPaidForDocket(did);

        await pb.collection("dockets").update(did, {
          due: 0,
          status: "Paid",
        });

        this.MARK_AS_PAID = {
          isVisible: false,
          message: "",
          onClick: () => {},
        };

        router.push('/pos');
      } catch (e) {
        console.error(e)
        toast.error("Failed to mark docket as paid", {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    },
  },
  getters: {
    getBookingRecords(state) {
      return (
        state?.originalDocketItems?.filter((item: any) => item?.booking && item.transactionType != 'Product') || []
      );
    },
  },
  persist: true,
});
